import { Alert, Button, Card, Field, PageHeader, useToast } from '@catering/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getRouteApi } from '@tanstack/react-router';
import { Plus, UtensilsCrossed, type LucideIcon } from 'lucide-react';
import {
  type ChangeEventHandler,
  type FocusEventHandler,
  type JSX,
  forwardRef,
  useEffect,
} from 'react';
import { type Resolver, useForm } from 'react-hook-form';

import { FormSection } from '../../components/form-section';
import type { MenuFormInput, ServiceType } from '../../menus/api';
import { createMenu, listServiceTypes } from '../../menus/api';
import {
  DEFAULT_FORM_VALUES,
  type FieldErrorTarget,
  type MenuFormFields,
  type MenuFormValues,
  mapMenuFormError,
  menuFormSchema,
  toFormInput,
} from './menu-form-schema';

const menuNewRoute = getRouteApi('/protected/menus/new');

/**
 * `exactOptionalPropertyTypes` treats an explicit `error={undefined}` as a
 * different (rejected) shape than the prop being absent; this omits the key
 * entirely instead of ever assigning `undefined` to it.
 */
function maybeError(message: string | undefined): { error: string } | Record<string, never> {
  return message === undefined ? {} : { error: message };
}

export interface MenuFormProps {
  serviceTypes: ServiceType[];
  defaultValues?: MenuFormFields;
  submitLabel: string;
  /** Decorative: aria-hidden, same convention as `Button`'s own `icon` prop. */
  submitIcon?: LucideIcon;
  onSubmit: (values: MenuFormValues) => Promise<void>;
  submitting: boolean;
  formError?: string;
  fieldError?: { field: FieldErrorTarget; message: string };
  onCancel?: () => void;
}

/**
 * `menus/spec.md` → "Pantallas de alta, edición y detalle en back-office".
 * Used both by `/menus/new` and by `MenuInfoTab`'s edit mode.
 */
export function MenuForm({
  serviceTypes,
  defaultValues,
  submitLabel,
  submitIcon,
  onSubmit,
  submitting,
  formError,
  fieldError,
  onCancel,
}: MenuFormProps): JSX.Element {
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<MenuFormFields, unknown, MenuFormValues>({
    resolver: zodResolver(menuFormSchema) as unknown as Resolver<
      MenuFormFields,
      unknown,
      MenuFormValues
    >,
    defaultValues: defaultValues ?? DEFAULT_FORM_VALUES,
  });

  useEffect(() => {
    if (fieldError !== undefined) {
      setError(fieldError.field, { type: 'server', message: fieldError.message });
    }
  }, [fieldError, setError]);

  // Always include the currently assigned service type, even if it has since
  // been deactivated: an edit form that drops the selected option out from
  // under the user silently changes the menu's service type on submit.
  const selectableServiceTypes = serviceTypes.filter(
    (serviceType) => serviceType.isActive || serviceType.id === defaultValues?.serviceTypeId,
  );

  return (
    <form
      onSubmit={(event) => void handleSubmit((values) => onSubmit(values))(event)}
      className="flex flex-col gap-6"
      noValidate
    >
      {formError !== undefined ? <Alert tone="danger">{formError}</Alert> : null}

      <FormSection title="Identificación">
        <Field
          label="Código"
          required
          {...register('code')}
          {...maybeError(errors.code?.message)}
        />
        <Field
          label="Nombre"
          required
          {...register('name')}
          {...maybeError(errors.name?.message)}
        />
        <SelectField
          label="Tipo de servicio"
          {...register('serviceTypeId')}
          {...maybeError(errors.serviceTypeId?.message)}
          options={selectableServiceTypes.map((serviceType) => ({
            value: serviceType.id,
            label: serviceType.isActive ? serviceType.name : `${serviceType.name} (inactivo)`,
          }))}
        />
        <Field
          label="Precio base (en pesos)"
          required
          placeholder="1500.00"
          {...register('basePrice')}
          {...maybeError(errors.basePrice?.message)}
        />
      </FormSection>

      <FormSection title="Detalle">
        <div className="sm:col-span-2">
          <Field
            label="Descripción"
            {...register('description')}
            {...maybeError(errors.description?.message)}
          />
        </div>
        <Field
          label="URL de imagen"
          {...register('imageUrl')}
          {...maybeError(errors.imageUrl?.message)}
        />
        <div className="sm:col-span-2">
          <Field
            label="Notas de preparación"
            {...register('preparationNotes')}
            {...maybeError(errors.preparationNotes?.message)}
          />
        </div>
      </FormSection>

      <div className="flex items-center justify-end gap-3">
        {onCancel === undefined ? null : (
          <Button type="button" variant="secondary" onClick={onCancel}>
            Cancelar
          </Button>
        )}
        <Button
          type="submit"
          {...(submitIcon === undefined ? {} : { icon: submitIcon })}
          loading={submitting}
        >
          {submitLabel}
        </Button>
      </div>
    </form>
  );
}

interface SelectFieldOption {
  value: string;
  label: string;
}

interface SelectFieldProps {
  label: string;
  name: string;
  error?: string | undefined;
  options: SelectFieldOption[];
  onChange: ChangeEventHandler<HTMLSelectElement>;
  onBlur: FocusEventHandler<HTMLSelectElement>;
}

/**
 * No shared `Select` component yet (`design.md` §7); mirrors
 * `CustomerForm`'s `SelectField`, including the `forwardRef` requirement
 * `register()` needs to actually update the value.
 */
const SelectField = forwardRef<HTMLSelectElement, SelectFieldProps>(function SelectField(
  { label, name, error, options, onChange, onBlur },
  ref,
) {
  const selectId = `menu-form-${name}`;
  const messageId = `${selectId}-message`;
  const hasError = error !== undefined;

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={selectId} className="text-sm font-medium text-slate-900">
        {label}
        <span aria-hidden="true" className="ml-0.5 text-red-600">
          *
        </span>
      </label>
      <select
        id={selectId}
        name={name}
        ref={ref}
        onChange={onChange}
        onBlur={onBlur}
        className={`h-11 rounded-md border px-3 text-sm ${hasError ? 'border-red-500' : 'border-slate-300'}`}
        aria-invalid={hasError}
        aria-describedby={hasError ? messageId : undefined}
      >
        <option value="">Seleccioná una opción</option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      {hasError ? (
        <p id={messageId} role="alert" className="text-xs text-red-600">
          {error}
        </p>
      ) : null}
    </div>
  );
});

/** `menus/spec.md` → "Alta de menú", "Pantallas de alta, edición y detalle en back-office". */
export function MenuFormPage(): JSX.Element {
  const navigate = menuNewRoute.useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const serviceTypesQuery = useQuery({ queryKey: ['service-types'], queryFn: listServiceTypes });

  const mutation = useMutation({
    mutationFn: (input: MenuFormInput) => createMenu(input),
    onSuccess: async (menu) => {
      await queryClient.invalidateQueries({ queryKey: ['menus'] });
      toast('success', 'Menú creado');
      await navigate({ to: '/menus/$menuId', params: { menuId: menu.id } });
    },
  });

  const { formError, fieldError } = mapMenuFormError(mutation.error);

  async function handleSubmit(values: MenuFormValues): Promise<void> {
    // `mutation.error` (read via `mapMenuFormError` above) is the source of
    // truth for the UI; the rejection itself would otherwise be an
    // unhandled promise.
    await mutation.mutateAsync(toFormInput(values)).catch(() => undefined);
  }

  return (
    <section className="flex max-w-2xl flex-col gap-6">
      <PageHeader
        icon={UtensilsCrossed}
        title="Nuevo menú"
        description="Carguá la información y el precio base del nuevo menú del catálogo."
      />
      <Card>
        {serviceTypesQuery.isError ? (
          <Alert tone="danger" title="No pudimos cargar los tipos de servicio">
            <div className="flex items-center justify-between gap-4">
              <span>Ocurrió un error al cargar. Intentá de nuevo.</span>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => void serviceTypesQuery.refetch()}
              >
                Reintentar
              </Button>
            </div>
          </Alert>
        ) : (
          <MenuForm
            serviceTypes={serviceTypesQuery.data ?? []}
            submitLabel="Crear menú"
            submitIcon={Plus}
            submitting={mutation.isPending}
            onSubmit={handleSubmit}
            {...(formError === undefined ? {} : { formError })}
            {...(fieldError === undefined ? {} : { fieldError })}
          />
        )}
      </Card>
    </section>
  );
}
